<template>
  <FormField :id="id" :label="label" :info="info" :error="error">
    <div class="relative">
      <!-- Click opens the picker right away: nobody types a contract end date. On click only, not
           on focus, so tabbing through the form never pops it open uninvited. -->
      <!-- No value binding: flatpickr owns the text, and a bound value (even undefined) would wipe
           it on every re-render. -->
      <input
        :id="id"
        ref="input"
        type="text"
        placeholder="dd/mm/jjjj"
        :disabled="disabled || locked"
        :aria-invalid="error ? true : undefined"
        :aria-describedby="error ? `${id}-error` : undefined"
        class="h-14 w-full appearance-none rounded-xl border px-4 pr-12 text-base font-normal outline-none transition-colors placeholder:text-line-strong focus:border-blue-bright disabled:cursor-not-allowed [&:read-only]:cursor-pointer"
        :class="[off ? 'bg-surface-muted' : 'bg-white', error ? 'border-red-600' : 'border-line', textColour]"
        @input="onInput"
        @click="openPicker"
      />
      <!-- The calendar icon from the design. Deliberately not a button: the input is the control
           and the only tab stop, this is a pointer shortcut on top of it. Transparent, so it can
           never cover the border of the field. -->
      <div
        aria-hidden="true"
        class="date-field-button absolute inset-y-0 right-0 flex items-center justify-end pr-4"
        :class="[off ? 'cursor-not-allowed text-muted' : 'cursor-pointer text-label']"
        @click="openPicker"
      >
        <CalendarIcon class="size-6" />
      </div>
    </div>
  </FormField>
</template>

<script setup lang="ts">
import flatpickr from 'flatpickr'
import { Dutch } from 'flatpickr/dist/l10n/nl.js'
import type { Instance } from 'flatpickr/dist/types/instance'
import { computed, onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue'

import CalendarIcon from '@/assets/svg/calendar.svg?component'
import FormField from '@/components/form/FormField.vue'

const props = withDefaults(
  defineProps<{
    id: string
    label: string
    /** ISO date (yyyy-mm-dd), the format the store speaks. Empty means not filled in. */
    modelValue: string
    /** Earliest selectable ISO date; the calendar greys out everything before it. */
    min?: string
    info?: string
    error?: string
    /** Not applicable right now: greyed out. */
    disabled?: boolean
    /** Frozen while the calculation runs: greyed out, but the value stays readable. */
    locked?: boolean
  }>(),
  { min: undefined, info: undefined, error: undefined, disabled: false, locked: false },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

// On hold or not applicable: the field steps back, so it is clear why it does not respond.
const off = computed(() => props.disabled || props.locked)

const textColour = computed(() => {
  if (props.disabled) {
    return 'text-muted'
  }

  return props.locked ? 'text-body' : 'text-label'
})

const input = useTemplateRef('input')

/**
 * The flatpickr popup from the keuze engine, on every device. A native date input was the mobile
 * control at first, but iOS ignores the minimum date it is given and lets a visitor pick a day in
 * the past anyway (#6331), which the calendar below does block. The field is a text input flatpickr
 * owns completely: only text can carry the dd/mm/jjjj placeholder and the matching display format,
 * things a native date input cannot show. The store keeps speaking ISO through the emits. The
 * calendar renders in static mode inside the field's wrapper: that keeps it within the mount point
 * the scoped styles can reach (see postcss.config.js). Static mode never repositions, so onOpen
 * flips the popup above the field itself when the viewport has no room below (flatpickr's own
 * dynamic positioning miscalculates inside a positioned container).
 */
let picker: Instance | null = null

// Parsed per part: new Date('yyyy-mm-dd') reads the string as UTC midnight, which flatpickr then
// shows in local time, a day early in timezones west of UTC.
function toLocalDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)

  return new Date(year, month - 1, day)
}

onMounted(() => {
  picker = flatpickr(input.value!, {
    locale: Dutch,
    dateFormat: 'd/m/Y',
    // Without this flatpickr hands a touch device back to the native picker, the one that lets a
    // day in the past through.
    disableMobile: true,
    // Dates as Date objects: flatpickr reads strings in the display format, not as ISO.
    defaultDate: props.modelValue ? toLocalDate(props.modelValue) : undefined,
    minDate: props.min ? toLocalDate(props.min) : undefined,
    static: true,
    monthSelectorType: 'static',
    prevArrow: '«',
    nextArrow: '»',
    onOpen: (_dates, _value, instance) => {
      const field = input.value!.getBoundingClientRect()
      const needed = instance.calendarContainer.offsetHeight + 8

      instance.calendarContainer.classList.toggle(
        'popup-above',
        field.bottom + needed > window.innerHeight && field.top > needed,
      )
    },
  })
})

/**
 * flatpickr echoes every pick through an input event in its display format (dd/mm/jjjj); normalise
 * that back to ISO. Emitting the display format as-is would poison the store, whose ISO value feeds
 * the popup right back.
 */
function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value

  emit('update:modelValue', value.includes('/') ? value.split('/').reverse().join('-') : value)
}

onBeforeUnmount(() => picker?.destroy())

// The store can change the value from outside the popup, like the contract type clearing the
// date; the popup follows. As a Date object: an ISO string would be read in the display format.
watch(
  () => props.modelValue,
  (value) => {
    if (!picker) {
      return
    }

    if (value) {
      picker.setDate(toLocalDate(value), false)
    } else {
      picker.clear(false)
    }
  },
)

// A field that freezes mid-interaction takes its popup with it.
watch(off, (frozen) => {
  if (frozen) {
    picker?.close()
  }
})

function openPicker() {
  // The overlay is not a control, so it carries no disabled state of its own.
  if (off.value) {
    return
  }

  picker?.open()
}
</script>
